/**
 * Curtain Lootbox - Animated curtain reveal for mystery deals
 * Features pre-rendered video animations with tier-colored glows
 * Replaces MysteryBoxCard with theatrical curtain reveal experience
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Lock, MapPin, Clock, HelpCircle, Sparkles } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthStore } from '../../../../stores/auth-store';
import { useCanUnlock, useRumioBalance } from '../hooks/useRumios';
import type { DealProduct, MysteryType } from '../types';
import { MYSTERY_TIERS, RUMIO_CONFIG } from '../utils/constants';
import { getMysteryCountdown } from '../utils/mystery';

interface Props {
  product: DealProduct;
  mysteryType: MysteryType;
  onUnlock: (product: DealProduct, type: MysteryType) => void;
  onPurchase: (type: MysteryType) => void;
  onLoginRequired: () => void;
  index?: number;
}

// Video paths for each state and tier
const CURTAIN_VIDEOS = {
  idle: '/animations/curtains/curtain-idle.webm',
  reveal: {
    super: '/animations/curtains/curtain-reveal-super.webm',
    fresh: '/animations/curtains/curtain-reveal-fresh.webm',
    standard: '/animations/curtains/curtain-reveal-standard.webm',
  },
};

type RevealState = 'locked' | 'revealing' | 'revealed';

export default function CurtainLootbox({
  product,
  mysteryType,
  onUnlock,
  onPurchase,
  onLoginRequired,
  index = 0,
}: Props) {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const { data: balance } = useRumioBalance();
  const canUnlock = useCanUnlock(mysteryType);

  const [revealState, setRevealState] = useState<RevealState>('locked');
  const [isHovered, setIsHovered] = useState(false);

  const idleVideoRef = useRef<HTMLVideoElement>(null);
  const revealVideoRef = useRef<HTMLVideoElement>(null);

  const tierConfig = MYSTERY_TIERS[mysteryType];
  const rumioConfig = RUMIO_CONFIG[mysteryType];
  const countdown = getMysteryCountdown(product.first_seen, user?.subscription_tier || null);
  // Use unified balance instead of tier-specific
  const currentBalance = balance?.balance ?? 0;

  // Preload reveal video on mount
  useEffect(() => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.src = CURTAIN_VIDEOS.reveal[mysteryType];
    video.load();
  }, [mysteryType]);

  const handleUnlockClick = useCallback(() => {
    if (revealState !== 'locked') return;

    if (!user) {
      onLoginRequired();
      return;
    }

    if (canUnlock) {
      // Start reveal animation
      setRevealState('revealing');

      // Pause idle video
      if (idleVideoRef.current) {
        idleVideoRef.current.pause();
      }

      // Play reveal video
      if (revealVideoRef.current) {
        revealVideoRef.current.currentTime = 0;
        revealVideoRef.current.play().catch(console.error);
      }

      // Trigger actual unlock after animation completes
      // Animation is 90 frames at 30fps = 3 seconds
      setTimeout(() => {
        onUnlock(product, mysteryType);
        setRevealState('revealed');
      }, 2800);
    } else {
      onPurchase(mysteryType);
    }
  }, [user, canUnlock, product, mysteryType, onUnlock, onPurchase, onLoginRequired, revealState]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleUnlockClick();
    }
  };

  return (
    <div
      className="group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleUnlockClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div
        className={`relative rounded-2xl overflow-hidden border-2 ${tierConfig.borderColor} transition-all duration-500 ${
          isHovered && revealState === 'locked' ? 'scale-[1.02] shadow-2xl' : 'shadow-lg'
        }`}
        style={{
          boxShadow: isHovered
            ? `0 0 30px ${tierConfig.glowColor}, 0 0 60px ${tierConfig.glowColor}40`
            : `0 0 15px ${tierConfig.glowColor}60`,
        }}
      >
        {/* Video Container */}
        <div className="aspect-square relative bg-gray-900 overflow-hidden">
          {/* Blurred Product Preview (behind curtain, visible when revealing) */}
          {product.image_url && (
            <div
              className={`absolute inset-0 bg-center bg-contain bg-no-repeat transition-all duration-1000 ${
                revealState === 'locked' ? 'opacity-10 blur-lg scale-110' : 'opacity-40 blur-sm scale-105'
              }`}
              style={{ backgroundImage: `url(${product.image_url})`, zIndex: 0 }}
            />
          )}

          {/* Idle Video (looping, shown when locked) */}
          <video
            ref={idleVideoRef}
            src={CURTAIN_VIDEOS.idle}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-500 ${
              revealState === 'locked' ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          />

          {/* Reveal Video (one-shot, shown when revealing/revealed) */}
          <video
            ref={revealVideoRef}
            src={CURTAIN_VIDEOS.reveal[mysteryType]}
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-300 ${
              revealState !== 'locked' ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onEnded={() => setRevealState('revealed')}
          />

          {/* Sparkle Overlay (animated) */}
          {revealState === 'locked' && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
              {[...Array(6)].map((_, i) => (
                <Sparkles
                  key={i}
                  className="absolute w-4 h-4 text-white/30 animate-pulse"
                  style={{
                    left: `${15 + i * 15}%`,
                    top: `${10 + (i % 3) * 25}%`,
                    animationDelay: `${i * 200}ms`,
                    animationDuration: '2s',
                  }}
                />
              ))}
            </div>
          )}

          {/* Tier Badge */}
          <div className="absolute top-3 left-3 z-30">
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white ${
                mysteryType === 'super'
                  ? 'bg-amber-500/90'
                  : mysteryType === 'fresh'
                  ? 'bg-purple-500/90'
                  : 'bg-blue-500/90'
              } backdrop-blur-sm shadow-lg`}
            >
              <Lock className="w-3.5 h-3.5" />
              {tierConfig.name}
            </div>
          </div>

          {/* As-Is Badge */}
          <div className="absolute top-3 right-3 z-30">
            <div className="px-2.5 py-1 bg-emerald-500/90 text-white text-xs font-semibold rounded-full backdrop-blur-sm shadow-lg">
              {t('deals.category.asIs', 'As-Is')}
            </div>
          </div>

          {/* Countdown Timer */}
          {countdown && revealState === 'locked' && (
            <div className="absolute bottom-3 left-3 right-3 z-30">
              <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-black/60 text-white text-xs rounded-full backdrop-blur-sm">
                <Clock className="w-3 h-3" />
                {countdown}
              </div>
            </div>
          )}

          {/* Revealing State Overlay */}
          {revealState === 'revealing' && (
            <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
              <div className="animate-pulse">
                <span className="text-5xl">{tierConfig.icon}</span>
              </div>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className={`p-4 bg-gradient-to-br ${tierConfig.bgColor}`}>
          {/* Title */}
          <div className="flex items-center gap-2 mb-1">
            <HelpCircle className="w-4 h-4 text-white/60" />
            <h3 className="font-bold text-white text-lg">
              {t('deals.mysteryDeal', 'Mystery Deal')}
            </h3>
          </div>

          {/* Age Range */}
          <p className="text-white/70 text-sm mb-3">{tierConfig.ageRange}</p>

          {/* Store Info */}
          <div className="flex items-center gap-1.5 text-white/60 text-xs mb-4">
            <MapPin className="w-3.5 h-3.5" />
            <span>{product.store_name}</span>
            {product.store_city && <span>• {product.store_city}</span>}
          </div>

          {/* Unlock Button */}
          <button
            disabled={revealState !== 'locked'}
            className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              revealState !== 'locked'
                ? 'bg-gray-500/50 text-white/50 cursor-not-allowed'
                : canUnlock
                ? 'bg-white text-gray-900 hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
            }`}
          >
            {revealState === 'revealing' ? (
              <>
                <span className="animate-spin">✨</span>
                {t('deals.revealing', 'Revealing...')}
              </>
            ) : revealState === 'revealed' ? (
              <>
                <span>🎉</span>
                {t('deals.revealed', 'Revealed!')}
              </>
            ) : user ? (
              canUnlock ? (
                <>
                  <span>{rumioConfig.icon}</span>
                  {t('deals.unlockWith', 'Unlock with 1 {rumio}').replace('{rumio}', rumioConfig.name)}
                </>
              ) : (
                <>
                  <span>{rumioConfig.icon}</span>
                  {t('deals.getRumios', 'Get {rumio}s to Unlock').replace('{rumio}', rumioConfig.name)}
                </>
              )
            ) : (
              <>
                <Lock className="w-4 h-4" />
                {t('deals.signInToUnlock', 'Sign in to Unlock')}
              </>
            )}
          </button>

          {/* Balance Info (if logged in and locked) */}
          {user && revealState === 'locked' && (
            <p className="mt-2 text-center text-xs text-white/50">
              {t('deals.youHave', 'You have')} {currentBalance} {rumioConfig.namePlural}
            </p>
          )}
        </div>

        {/* Pulsing Glow Ring */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse-glow"
          style={{
            boxShadow: `inset 0 0 20px ${tierConfig.glowColor}40`,
          }}
        />
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
